import { z } from "zod";

/** Every error response from the API has this shape. */
export const ERROR_CODES = [
  "BAD_REQUEST",
  "VALIDATION_FAILED",
  "UNAUTHENTICATED",
  "TWO_FACTOR_REQUIRED",
  "FORBIDDEN",
  "NOT_FOUND",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "IDEMPOTENCY_CONFLICT",
  "RATE_LIMITED",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  /**
   * 402: the workspace is read-only — paused or cancelled by Lean2Automate, or
   * its licence and grace period have run out. `details`: WorkspaceSuspendedDetails.
   */
  "WORKSPACE_SUSPENDED",
  /** 409: every staff seat the licence allows is taken. `details`: { limit, used }. */
  "SEAT_LIMIT_REACHED",
  "INTEGRATION_NOT_CONFIGURED",
  "UPSTREAM_ERROR",
  "INTERNAL",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export const apiErrorSchema = z.object({
  error: z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof apiErrorSchema>;

/** Offset pagination for tables (max 200 per page). Append-only feeds (audit, activity) use cursors. */
export const listQuerySchema = z.object({
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
  sort: z.string().max(64).optional(),
  order: z.enum(["asc", "desc"]).default("desc"),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), total: z.number().int(), limit: z.number().int(), offset: z.number().int() });
}
export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export const cursorQuerySchema = z.object({
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export function cursorPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}
export interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
}

export const uuidSchema = z.string().uuid();
export const idParamSchema = z.object({ id: uuidSchema });
/** Calendar date without time, "YYYY-MM-DD". */
export const dateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
/** ISO-8601 timestamp string with timezone. */
export const timestampSchema = z.string().datetime({ offset: true });
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const phoneSchema = z.string().trim().regex(/^\+?[0-9 ()-]{6,20}$/, "Invalid phone number");
export const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
export const tagsSchema = z.array(z.string().trim().min(1).max(40)).max(30);

/** Header names shared by client and server. */
export const HEADERS = {
  csrf: "x-l2a-csrf",
  idempotencyKey: "idempotency-key",
  requestId: "x-request-id",
} as const;

export const API_PREFIX = "/api/v1";
export const PORTAL_API_PREFIX = "/api/portal/v1";
